import { Component, Inject, Input, OnInit } from '@angular/core';
import { LayoutConfig, NavItem } from '../../models';
import { LAYOUT_CONFIG_TOKEN } from '../../services/layout.token';

/**
 * A footer entry. A bare string renders as plain text; a `NavItem` renders as a
 * real anchor.
 *
 * Both forms are accepted because the entries were plain strings before, and
 * every consuming app still relies on those defaults. Nothing in this workspace
 * knows the destination of "Mass Payments" or "FAQ" yet, so the defaults stay
 * label-only rather than shipping invented URLs — an app supplies `NavItem`s
 * once it has them and the same markup becomes navigable.
 */
export type FooterEntry = string | NavItem;

/** One resolved column, ready to render. */
export interface FooterColumn {
  readonly heading: string;
  readonly entries: readonly ResolvedFooterEntry[];
}

export interface ResolvedFooterEntry {
  readonly label: string;
  readonly link?: string;
  readonly external?: boolean;
}

@Component({
  standalone: false,
  selector: 'sub-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit {
  @Input() logoUrl = ``;
  @Input() logoLink = '/home';
  @Input() currentYear = new Date().getFullYear().toString();
  @Input() products: FooterEntry[] = ['Seech for Startups', 'Seech for Developers'];
  @Input() payments: FooterEntry[] = ['Mass Payments', 'Assets'];
  @Input() support: FooterEntry[] = ['FAQ', 'Contact'];
  @Input() slogan = 'Aspire Innovate Rule ...';

  /** Headings are inputs so a subsite can retitle a column without a fork. */
  @Input() productsHeading = 'Seech Products';
  @Input() paymentsHeading = 'Seech Payments';
  @Input() supportHeading = 'Support';

  /** Optional bottom-row entries — privacy, terms, and the like. */
  @Input() legal: FooterEntry[] = [];

  constructor(@Inject(LAYOUT_CONFIG_TOKEN) private config: LayoutConfig) {}

  ngOnInit(): void {
    this.logoUrl = `${this.config.globalCdnUrl}/images/seech-logo.png`;
  }

  /**
   * The link columns as one list.
   *
   * Built as data rather than as three near-identical blocks of markup: the
   * columns share a grid track each, and repeating the markup is how their
   * spacing drifted apart in the first place. An empty column is dropped so a
   * subsite that clears one does not leave a gap in the row.
   */
  get columns(): FooterColumn[] {
    return [
      { heading: this.productsHeading, entries: this.resolve(this.products) },
      { heading: this.paymentsHeading, entries: this.resolve(this.payments) },
      { heading: this.supportHeading, entries: this.resolve(this.support) },
    ].filter((column) => column.entries.length > 0);
  }

  get legalEntries(): ResolvedFooterEntry[] {
    return this.resolve(this.legal);
  }

  private resolve(entries: FooterEntry[] | null | undefined): ResolvedFooterEntry[] {
    return (entries ?? [])
      .map((entry) =>
        typeof entry === 'string'
          ? { label: entry }
          : { label: entry.label, link: entry.link, external: entry.external },
      )
      .filter((entry) => !!entry.label);
  }
}
